import { memo, type ComponentPropsWithoutRef } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { decodeHtmlEntities } from '@paladin-monorepo/common';
import { cn } from '@/lib/utils';

interface MarkdownContentProps {
  content: string;
  className?: string;
}

const components: Components = {
  p({ children }) {
    return <p className="mb-2 last:mb-0">{children}</p>;
  },
  a({ href, children }) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:opacity-80">
        {children}
      </a>
    );
  },
  ul({ children }) {
    return <ul className="mb-2 ml-4 list-disc last:mb-0 [&_ul]:mb-0">{children}</ul>;
  },
  ol({ children }) {
    return <ol className="mb-2 ml-4 list-decimal last:mb-0 [&_ol]:mb-0">{children}</ol>;
  },
  li({ children }) {
    return <li className="mb-0.5">{children}</li>;
  },
  h1({ children }) {
    return <h1 className="mb-2 text-lg font-bold">{children}</h1>;
  },
  h2({ children }) {
    return <h2 className="mb-2 text-base font-bold">{children}</h2>;
  },
  h3({ children }) {
    return <h3 className="mb-1.5 text-sm font-semibold">{children}</h3>;
  },
  blockquote({ children }) {
    return <blockquote className="border-l-2 border-current/30 pl-3 italic opacity-80">{children}</blockquote>;
  },
  table({ children }) {
    return (
      <div className="my-2 overflow-x-auto">
        <table className="min-w-full border-collapse text-sm">{children}</table>
      </div>
    );
  },
  th({ children }) {
    return <th className="border border-current/20 px-2 py-1 text-left font-semibold">{children}</th>;
  },
  td({ children }) {
    return <td className="border border-current/20 px-2 py-1">{children}</td>;
  },
  hr() {
    return <hr className="my-3 border-current/20" />;
  },
  code({ className, children, ...rest }: ComponentPropsWithoutRef<'code'>) {
    const match = /language-(\w+)/.exec(className || '');
    const codeString = String(children).replace(/\n$/, '');

    if (match) {
      return (
        <div className="group relative my-2 overflow-hidden rounded-lg">
          <div className="flex items-center justify-between bg-black/80 px-3 py-1 text-xs text-white/60">
            <span>{match[1]}</span>
          </div>
          <SyntaxHighlighter
            style={oneDark}
            language={match[1]}
            PreTag="div"
            customStyle={{ margin: 0, borderRadius: 0, fontSize: '0.8125rem' }}
            {...rest}
          >
            {codeString}
          </SyntaxHighlighter>
        </div>
      );
    }

    return (
      <code className="rounded bg-black/10 px-1.5 py-0.5 text-[0.8125rem] dark:bg-white/10" {...rest}>
        {children}
      </code>
    );
  },
};

const remarkPlugins = [remarkGfm];

export const MarkdownContent = memo(function MarkdownContent({ content, className }: MarkdownContentProps) {
  return (
    <div className={cn('text-sm leading-relaxed', className)}>
      <ReactMarkdown remarkPlugins={remarkPlugins} components={components}>
        {decodeHtmlEntities(content)}
      </ReactMarkdown>
    </div>
  );
});
